import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { 
  Popover, 
  Typography, 
  Avatar, 
  Divider, 
  Button, 
  useTheme,
  TextField,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Tooltip
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { FaceOutlined as FaceIcon } from "@mui/icons-material";
import { AuthContext } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import ApiService, { setCSRFToken } from "../../services/api";
import { PROFILE_URL } from "../../constants/routerUrl";

const useStyles = makeStyles((theme) => ({
  popover: {
    background: theme.custom.palette.profilePopColor,
    width: theme.spacing(40),
    borderRadius: theme.shape.borderRadius,
  },
  container: {
    display: "flex",
    padding: theme.spacing(2),
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    background: theme.palette.background.default,
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
  },
  userName: {
    ...theme.custom.fontFamily.metropolis,
    fontSize: "1rem",
    fontWeight: 500,
  },
  userEmail: {
    ...theme.custom.fontFamily.roboto,
    fontSize: "0.9rem",
  },
  bar: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonSignout: {
    ...theme.custom.fontFamily.metropolis,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    fontSize: "0.8rem",
    fontWeight: 500,
    textTransform: "none",
  },
  loginContainer: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.background.highlight 
      : '#fafafa',
    '& fieldset': {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.secondary.dark,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.secondary.main,
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 10,
  textTransform: 'none',
  padding: theme.spacing(1, 3),
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(255, 193, 7, 0.3)'
      : '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

export default function ProfilePopover({ anchorEl, isOpen, onClose }) {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const id = isOpen ? "profile-popover" : undefined;
  const auth = useContext(AuthContext);
  const { showSuccess, showError } = useNotification();
  
  const [loginStep, setLoginStep] = useState('initial');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Track cooldown per phone number
  const [phoneCooldowns, setPhoneCooldowns] = useState({});
  const cooldownRef = useRef(null);

  // Get cooldown for current phone number
  const getCurrentCooldown = useCallback(() => {
    return phoneCooldowns[phoneNumber] || 0;
  }, [phoneCooldowns, phoneNumber]);

  useEffect(() => {
    const currentCooldown = getCurrentCooldown();
    
    if (currentCooldown > 0) {
      cooldownRef.current = setTimeout(() => {
        setPhoneCooldowns(prev => ({
          ...prev,
          [phoneNumber]: Math.max(0, prev[phoneNumber] - 1)
        }));
      }, 1000);
    }

    return () => {
      if (cooldownRef.current) {
        clearTimeout(cooldownRef.current);
      }
    };
  }, [phoneCooldowns, phoneNumber, getCurrentCooldown]);

  // Reset state when popover closes
  useEffect(() => {
    if (!isOpen) {
      setLoginStep('initial');
      setPhoneNumber('');
      setOtp('');
      setError('');
      // Don't reset cooldowns - they should persist
    }
  }, [isOpen]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    setLoginStep('initial');
    setPhoneNumber('');
    setOtp('');
    setError('');
    onClose();
  };

  const handleStartLogin = () => {
    setLoginStep('phone');
    setError('');
  };

  const handleSendOTP = async () => {
    if (!phoneNumber.trim()) {
      setError('দয়া করে ফোন নম্বর দিন');
      return;
    }

    const phoneRegex = /^(\+880|880|0)1[3-9]\d{8}$/;
    if (!phoneRegex.test(phoneNumber.replace(/[\s-]/g, ''))) {
      setError('দয়া করে একটি বৈধ বাংলাদেশী ফোন নম্বর দিন');
      return;
    }

    // Check cooldown for this phone number
    const currentCooldown = getCurrentCooldown();
    if (currentCooldown > 0) {
      setError(`অনুগ্রহ করে ${formatTime(currentCooldown)} পরে আবার চেষ্টা করুন`);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await ApiService.sendOTP(phoneNumber);
      
      // Set cooldown for this specific phone number
      setPhoneCooldowns(prev => ({
        ...prev,
        [phoneNumber]: 180
      }));
      
      setLoginStep('otp');
    } catch (err) {
      setError('OTP পাঠাতে ব্যর্থ হয়েছে');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('দয়া করে একটি বৈধ ৬-অঙ্কের OTP দিন');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await ApiService.verifyOTP(phoneNumber, otp);
      
      if (response.csrf_token) {
        setCSRFToken(response.csrf_token);
      }
      
      auth.login(response.user);
      
      // Clear cooldown on successful login
      setPhoneCooldowns(prev => {
        const newCooldowns = { ...prev };
        delete newCooldowns[phoneNumber];
        return newCooldowns;
      });
      
      showSuccess('সফলভাবে লগইন হয়েছে!');
      handleClose();
    } catch (err) {
      setError(err.message || 'ভুল OTP। দয়া করে আবার চেষ্টা করুন।');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    const currentCooldown = getCurrentCooldown();
    if (currentCooldown > 0) return;
    
    setError('');
    setLoading(true);
    try {
      await ApiService.sendOTP(phoneNumber);
      
      // Reset cooldown after resend
      setPhoneCooldowns(prev => ({
        ...prev,
        [phoneNumber]: 180
      }));
      
      setOtp('');
      showSuccess('OTP পুনরায় পাঠানো হয়েছে');
    } catch (err) {
      setError(err.message || 'OTP পুনরায় পাঠাতে ব্যর্থ হয়েছে');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
      showSuccess('সফলভাবে লগআউট হয়েছে');
      handleClose();
    } catch (error) {
      showError('লগআউট করতে সমস্যা হয়েছে');
    }
  };

  const renderLoggedIn = () => (
    <>
      <div className={classes.container}>
        <Tooltip title="প্রোফাইল দেখুন" arrow>
          <IconButton
            onClick={() => {
              navigate(PROFILE_URL);
              handleClose();
            }}
            sx={{ p: 0 }}
          >
            <Avatar alt={auth.user.name} className={classes.avatar}>
              <FaceIcon htmlColor={theme.custom.palette.iconColor} fontSize='large' />
            </Avatar>
          </IconButton>
        </Tooltip>
        <div className={classes.userInfo}>
          <Typography className={classes.userName} variant="h6" component="span" color="textPrimary">
            {auth.user.name || auth.user.phone}
          </Typography>
        </div>
      </div>
      <Divider />
      <div className={classes.bar}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleLogout}
          classes={{ root: classes.buttonSignout }}
        >
          লগ আউট
        </Button>
      </div>
    </>
  );

  const renderLoginInitial = () => (
    <>
      <div className={classes.container}>
        <Avatar alt="new User" className={classes.avatar}>
          <FaceIcon htmlColor={theme.custom.palette.iconColor} fontSize="large" />
        </Avatar>
        <div className={classes.userInfo}>
          <Typography
            className={classes.userEmail}
            variant="body1"
            component="span"
            color="textSecondary"
          >
            ফোন নাম্বার দিয়ে লগ ইন করুন
          </Typography>
        </div>
      </div>
      <Divider />
      <div className={classes.bar}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleStartLogin}
          classes={{ root: classes.buttonSignout }}
        >
          লগ ইন
        </Button>
      </div>
    </>
  );

  const renderPhoneInput = () => {
    const currentCooldown = getCurrentCooldown();
    
    return (
      <div className={classes.loginContainer}>
        <Typography variant="h6" fontWeight={600} textAlign="center" color="textPrimary">
          ফোন নম্বর দিন
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        
        {currentCooldown > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            আপনি {formatTime(currentCooldown)} পরে আবার OTP পাঠাতে পারবেন
          </Alert>
        )}

        <StyledTextField
          fullWidth
          label="ফোন নম্বর"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+8801XXXXXXXXX"
          disabled={loading}
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <ActionButton
            variant="outlined"
            onClick={() => setLoginStep('initial')}
            disabled={loading}
            color="secondary"
          >
            বাতিল
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={handleSendOTP}
            disabled={loading || !phoneNumber.trim() || currentCooldown > 0}
            color="secondary"
          >
            {loading ? <CircularProgress size={24} /> : 'OTP পাঠান'}
          </ActionButton>
        </Box>
      </div>
    );
  };

  const renderOTPInput = () => {
    const currentCooldown = getCurrentCooldown();
    
    return (
      <div className={classes.loginContainer}>
        <Typography variant="h6" fontWeight={600} textAlign="center" color="textPrimary">
          OTP যাচাই করুন
        </Typography>
        
        <Typography variant="body2" color="textSecondary" textAlign="center">
          আমরা একটি ৬-অঙ্কের OTP পাঠিয়েছি {phoneNumber} নম্বরে
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <StyledTextField
          fullWidth
          label="OTP লিখুন"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="০০০০০০"
          inputProps={{ maxLength: 6 }}
          disabled={loading}
        />

        <Box sx={{ textAlign: 'center' }}>
          <ActionButton
            variant="text"
            onClick={handleResendOTP}
            disabled={loading || currentCooldown > 0}
            size="small"
            color="secondary"
          >
            {loading ? <CircularProgress size={20} /> : 
              currentCooldown > 0 ? `পুনরায় পাঠান (${formatTime(currentCooldown)})` : 'OTP পুনরায় পাঠান'}
          </ActionButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <ActionButton
            variant="outlined"
            onClick={() => setLoginStep('phone')}
            disabled={loading}
            color="secondary"
          >
            পিছনে
          </ActionButton>
          <ActionButton
            variant="contained"
            onClick={handleVerifyOTP}
            disabled={loading || otp.length !== 6}
            color="secondary"
          >
            {loading ? <CircularProgress size={24} /> : 'ভেরিফাই করুন'}
          </ActionButton>
        </Box>
      </div>
    );
  };

  return (
    <div>
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        classes={{
          paper: classes.popover,
        }}
      >
        {auth.isLoggedIn ? renderLoggedIn() : (
          loginStep === 'initial' ? renderLoginInitial() :
          loginStep === 'phone' ? renderPhoneInput() :
          renderOTPInput()
        )}
      </Popover>
    </div>
  );
}
